import { getActiveGridTemplatesByUserProgramCategory } from '../../actions/gridTemplates/gridTemplatesActions';
import ENV_CONFIG from './environmentConfig';

// Enhanced localStorage wrapper for Azure environments
const safeLocalStorage = {
  getItem: (key) => {
    try {
      const item = localStorage.getItem(key);
      if (item === null || item === undefined) {
        return null;
      }
      
      // Check for corrupted data patterns common in Azure
      if (item === '[object Object]' || item === 'undefined' || item === 'null') {
        console.warn(`Corrupted localStorage data detected for key: ${key}, clearing...`);
        localStorage.removeItem(key);
        return null;
      }
      
      return item;
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      return null;
    }
  },
  
  setItem: (key, value) => {
    try {
      // Validate data before storing
      if (typeof value !== 'string') {
        console.error('Attempting to store non-string value in localStorage:', typeof value);
        return false;
      }
      
      // Test if it's valid JSON before storing
      if (value.startsWith('{') || value.startsWith('[')) {
        JSON.parse(value); // This will throw if invalid
      }
      
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error('Error storing in localStorage:', error);
      // Clear the key if storage fails
      try {
        localStorage.removeItem(key);
      } catch (clearError) {
        console.error('Error clearing localStorage:', clearError);
      }
      return false;
    }
  },
  
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  }
};

export const loadCurrentTemplate = async (storageKey, user, programId, categoryId) => {
  const isDev = process.env.NODE_ENV === 'development';
  try {
    // Try to load from localStorage first with enhanced error handling
    const localTemplate = safeLocalStorage.getItem(storageKey);
    if (localTemplate) {
      try {
        const parsedTemplate = JSON.parse(localTemplate);
        
        // Enhanced validation for Azure environment
        if (parsedTemplate && 
            typeof parsedTemplate === 'object' && 
            parsedTemplate.columns && 
            Array.isArray(parsedTemplate.columns) &&
            parsedTemplate.columns.length > 0) {
          
          if (isDev) console.log('Local template loaded with', parsedTemplate.columns.length, 'columns');
          return parsedTemplate;
        } else {
          if (isDev) console.warn('Invalid template structure in localStorage, clearing...');
          safeLocalStorage.removeItem(storageKey);
        }
      } catch (parseError) {
        console.error('Failed to parse template from localStorage:', parseError);
        safeLocalStorage.removeItem(storageKey);
      }
    }

    // Fallback to server template with timeout for Azure
    if (isDev) console.log('Loading template from server...');
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Server template load timeout')), 10000)
    );
    
    const serverTemplatePromise = getActiveGridTemplatesByUserProgramCategory(user.id, programId, categoryId);
    
    try {
      const serverTemplates = await Promise.race([serverTemplatePromise, timeoutPromise]);
      
      if (serverTemplates) {
        // Handle both single object and array responses
        let templateValue;
        if (Array.isArray(serverTemplates) && serverTemplates.length > 0) {
          templateValue = serverTemplates[0].templateValue;
        } else if (serverTemplates.templateValue) {
          templateValue = serverTemplates.templateValue;
        }
        
        if (templateValue) {
          try {
            const serverTemplate = JSON.parse(templateValue);
            ENV_CONFIG.log('Server template loaded with', serverTemplate.columns?.length || 0, 'columns');
            return serverTemplate;
          } catch (parseError) {
            console.error('Failed to parse server template:', parseError);
          }
        }
      }
    } catch (timeoutError) {
      console.warn('Server template load timed out, continuing without template');
    }

    ENV_CONFIG.log('No template found - returning null for default state');
    return null;
  } catch (error) {
    console.error('Error loading template:', error);
    return null;
  }
};

export const validateAndCleanTemplate = (template, currentPermissions) => {
  if (!template || !template.columns) {
    return template;
  }

  // Azure-optimized template validation
  const validatedTemplate = {
    ...template,
    columns: template.columns.filter(col => {
      // More strict validation for Azure environment
      return col &&
             typeof col === 'object' &&
             col.dataField &&
             typeof col.dataField === 'string' &&
             col.dataField.length > 0;
    }).map(col => {
      // Create clean, serializable column objects
      const cleanColumn = {
        dataField: String(col.dataField),
        visible: Boolean(col.visible !== false), // Default to true unless explicitly false
      };
      
      // Only add optional properties if they exist and are valid
      if (typeof col.visibleIndex === 'number') {
        cleanColumn.visibleIndex = col.visibleIndex;
      }
      if (typeof col.width === 'number' && col.width > 0) {
        cleanColumn.width = col.width;
      }
      if (typeof col.sortIndex === 'number') {
        cleanColumn.sortIndex = col.sortIndex;
      }
      if (col.sortOrder === 'asc' || col.sortOrder === 'desc') {
        cleanColumn.sortOrder = col.sortOrder;
      }
      if (col.filterValue !== undefined && col.filterValue !== null) {
        cleanColumn.filterValue = col.filterValue;
      }
      if (col.fixed === true) {
        cleanColumn.fixed = true;
      }
      if (col.fixedPosition === 'left' || col.fixedPosition === 'right') {
        cleanColumn.fixedPosition = col.fixedPosition;
      }
      
      return cleanColumn;
    })
  };

  // Only log in development to avoid blocking production
  if (process.env.NODE_ENV === 'development') {
    ENV_CONFIG.log('Template after validation:', validatedTemplate.columns?.length || 0, 'columns');
  }
  return validatedTemplate;
};

// Optimized saving with debouncing and environment-specific handling
let saveTimeout = null;
const getSaveDelay = () => {
  const config = ENV_CONFIG.getConfig();
  return config.autoSaveInterval / 60; // Usar una fracción del intervalo de auto-save
};

export const saveCurrentState = (gridInstance, storageKey) => {
  if (!gridInstance || !gridInstance.instance) {
    return;
  }

  // Clear any existing timeout to debounce rapid saves
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    try {
      const currentState = gridInstance.instance().state();
      if (!currentState || typeof currentState !== 'object') {
        console.warn('Invalid grid state, skipping save');
        return;
      }

      // Create Azure-optimized state object
      const optimizedState = {
        columns: (currentState.columns || []).map(col => ({
          dataField: String(col.dataField || ''),
          visible: Boolean(col.visible !== false),
          visibleIndex: typeof col.visibleIndex === 'number' ? col.visibleIndex : undefined,
          width: typeof col.width === 'number' && col.width > 0 ? col.width : undefined,
          sortIndex: typeof col.sortIndex === 'number' ? col.sortIndex : undefined,
          sortOrder: (col.sortOrder === 'asc' || col.sortOrder === 'desc') ? col.sortOrder : undefined,
          filterValue: (col.filterValue !== undefined && col.filterValue !== null) ? col.filterValue : undefined,
          fixed: col.fixed === true ? true : undefined,
          fixedPosition: (col.fixedPosition === 'left' || col.fixedPosition === 'right') ? col.fixedPosition : undefined
        })).filter(col => col.dataField.length > 0), // Remove invalid columns
        
        // Only include other state properties if they exist
        ...(currentState.searchText && { searchText: String(currentState.searchText) }),
        ...(currentState.expandedRowKeys && Array.isArray(currentState.expandedRowKeys) && { expandedRowKeys: currentState.expandedRowKeys }),
        ...(currentState.selectedRowKeys && Array.isArray(currentState.selectedRowKeys) && { selectedRowKeys: currentState.selectedRowKeys })
      };

      const stateString = JSON.stringify(optimizedState);
      
      // Validate JSON before storing
      JSON.parse(stateString); // This will throw if invalid
      
      const success = safeLocalStorage.setItem(storageKey, stateString);
      if (success) {
        ENV_CONFIG.log('saveCurrentState', storageKey, 'with', optimizedState.columns?.length || 0, 'columns');
      } else {
        console.error('Failed to save template state');
      }
    } catch (error) {
      console.error('Error saving template state:', error);
      // Clear potentially corrupted data
      safeLocalStorage.removeItem(storageKey);
    }
  }, getSaveDelay());
};

export const handleTemplateChange = async (gridInstance, storageKey, user, programId, categoryId, setTemplateError, setTemplateLoaded) => {
  try {
    // Add loading timeout for Azure environment
    const startTime = Date.now();
    const template = await loadCurrentTemplate(storageKey, user, programId, categoryId);
    const loadTime = Date.now() - startTime;
    
    ENV_CONFIG.log(`Template load took ${loadTime}ms`);
    
    if (template && gridInstance && gridInstance.instance) {
      const validatedTemplate = validateAndCleanTemplate(template, []);
      
      // Apply template with Azure-specific error handling
      try {
        // Use setTimeout to prevent blocking the UI thread
        setTimeout(() => {
          try {
            gridInstance.instance().state(validatedTemplate);
            setTemplateError(false);
            setTemplateLoaded(true);
            ENV_CONFIG.log('Template applied successfully');
          } catch (applyError) {
            console.error('Error applying template:', applyError);
            setTemplateError(true);
            safeLocalStorage.removeItem(storageKey);
          }
        }, 100);
      } catch (immediateError) {
        console.error('Immediate error in template application:', immediateError);
        setTemplateError(true);
        safeLocalStorage.removeItem(storageKey);
      }
    } else {
      ENV_CONFIG.log('No template available, allowing grid to continue');
      setTemplateLoaded(true);
    }
  } catch (error) {
    console.error('Error in handleTemplateChange:', error);
    setTemplateError(true);
    setTemplateLoaded(true);
  }
};

export const cleanFilterExpression = (filterExpression) => {
  return filterExpression;
};

// Environment diagnostics
export const diagnoseEnvironment = () => {
  const config = ENV_CONFIG.getConfig();
  
  ENV_CONFIG.log('Template Manager Environment Detected:', {
    environment: config.environment,
    hostname: window.location.hostname,
    userAgent: navigator.userAgent.substring(0, 100),
    templateLoadDelay: config.templateLoadDelay,
    autoSaveInterval: config.autoSaveInterval,
    localStorage: {
      available: typeof(Storage) !== "undefined",
      quota: navigator.storage ? 'Available' : 'Not Available'
    }
  });
  
  return config;
};